import { clientLogger } from '@/lib/clientLogger';
/**
 * TwilioVoiceManager — singleton wrapper around @capgo/capacitor-twilio-voice.
 *
 * Responsibilities:
 *  - Fetch a short-lived access token from the backend and init the SDK.
 *  - Schedule automatic token refresh before expiry.
 *  - Expose makeCall / endCall / muteCall / setSpeaker.
 *  - Fire event callbacks so React hooks can subscribe to call state changes.
 */

import { Capacitor } from '@capacitor/core';
import { CapacitorTwilioVoice } from '@capgo/capacitor-twilio-voice';
import { apiService } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CallState =
  | 'idle'
  | 'connecting'
  | 'ringing'
  | 'connected'
  | 'reconnecting'
  /** An inbound call is ringing this browser (web dialer receive mode; Telnyx web only). */
  | 'incoming'
  | 'ended';

export type AudioDeviceType = 'earpiece' | 'speaker' | 'bluetooth' | 'wired';

export interface AudioDevice {
  type: AudioDeviceType;
  name: string;
  /** Browser deviceId, set only by the web backend where several devices can share one type. */
  id?: string;
}

export interface VoiceCallEvent {
  state: CallState;
  callSid?: string;
  /** E.164 number or client identity being called */
  toE164?: string;
  /** E.164 caller ID used for the call */
  callerIdE164?: string;
  /** Human-readable display name for the callee */
  displayName?: string;
  isMuted?: boolean;
  /** Currently active audio output route */
  audioRoute?: AudioDeviceType;
  /** Non-terminal failure selecting/starting native call audio. */
  audioRouteError?: string;
  /** All available audio output devices */
  availableAudioDevices?: AudioDevice[];
  qualityWarnings?: string[];
  error?: string;
  /** Set on an 'ended' event that dropped uncleanly (network switch / dead socket) with the last-call
   *  params remembered, so the UI can offer a one-tap "Redial". Telnyx-only for now. */
  canRedial?: boolean;
  /** Inbound web-dialer calls only: everything the org already knows about the ringing call,
   *  delivered as X- SIP headers on the invite. `line` = WHICH of the org's numbers was dialed, so
   *  someone covering several lines knows what they're answering (its `company` is set only when
   *  exactly one company can use that number). `contactName` / `job` = who is calling and the job
   *  they're most likely about. Every part is optional — absent headers just mean a plainer card. */
  incomingContext?: {
    line?: { e164: string; name?: string; company?: string };
    contactName?: string;
    job?: { id: string; ref?: string };
  };
  /** Every live call on this tab, primary first — web dialer multi-call only (see CallSummary).
   *  Absent (undefined) when nothing is live, and on backends without concurrent calls. */
  calls?: CallSummary[];
}

/**
 * One entry of the web dialer's concurrent-call roster (desktop web + Telnyx only — the native
 * SDK cannot hold a second call, so native/Twilio backends never set `VoiceCallEvent.calls`).
 * The flat fields of `VoiceCallEvent` keep describing the PRIMARY call, so every consumer that
 * predates multi-call keeps working untouched; only the multi-call UI reads this list.
 */
export interface CallSummary {
  callSid: string;
  direction: 'inbound' | 'outbound';
  /** 'incoming' = ringing, not yet answered. 'held' = on hold (this call's audio is stopped). */
  status: 'incoming' | 'connecting' | 'ringing' | 'connected' | 'held' | 'reconnecting';
  /** The call the flat VoiceCallEvent fields describe — the one the main widget controls. */
  isPrimary: boolean;
  isMuted: boolean;
  remoteNumber?: string;
  displayName?: string;
  /** Epoch ms of this call's first 'active' — the UI ticks its own duration from this. */
  connectedAt?: number;
  incomingContext?: VoiceCallEvent['incomingContext'];
}

export type VoiceEventHandler = (event: VoiceCallEvent) => void;

// ─── Manager ──────────────────────────────────────────────────────────────────

class TwilioVoiceManager {
  private static _instance: TwilioVoiceManager | null = null;
  static get instance(): TwilioVoiceManager {
    if (!TwilioVoiceManager._instance) {
      TwilioVoiceManager._instance = new TwilioVoiceManager();
    }
    return TwilioVoiceManager._instance;
  }

  private _initialized = false;
  private _refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private _listeners: VoiceEventHandler[] = [];
  private _currentState: VoiceCallEvent = { state: 'idle' };

  // ── Public API ──────────────────────────────────────────────────────────────

  /** Subscribe to call state changes. Returns an unsubscribe function. */
  subscribe(handler: VoiceEventHandler): () => void {
    this._listeners.push(handler);
    // Immediately deliver current state so late subscribers are in sync
    handler({ ...this._currentState });
    return () => {
      this._listeners = this._listeners.filter((l) => l !== handler);
    };
  }

  /** Initialize the Twilio Voice SDK. Safe to call multiple times (no-ops after first). */
  async init(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    if (this._initialized) return;

    await this._fetchTokenAndLogin();
    await this._registerListeners();
    this._initialized = true;
  }

  /** Tear down SDK and clear any pending refresh timer. */
  async destroy(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    this._clearRefreshTimer();
    this._initialized = false;
    try {
      await CapacitorTwilioVoice.logout();
    } catch {
      // Ignore logout errors
    }
  }

  /**
   * Initiate an outbound call.
   * @param toE164 Destination E.164 phone number
   * @param callerIdE164 Caller ID (E.164) from one of the org's VoIP numbers
   * @param displayName Human-readable label shown in the UI / CallKit Recents
   * @param jobId Optional job ID to link this call to once it is recorded
   */
  async makeCall(
    toE164: string,
    callerIdE164: string,
    displayName?: string,
    jobId?: string,
  ): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      clientLogger.warn('[TwilioVoice] makeCall is only supported on native platforms.');
      return;
    }

    // Ensure SDK is ready
    if (!this._initialized) {
      await this.init(); // registers with normal identity
    }

    // Request microphone permission upfront before any token work
    const { granted } = await CapacitorTwilioVoice.checkMicrophonePermission();
    if (!granted) {
      const { granted: g2 } = await CapacitorTwilioVoice.requestMicrophonePermission();
      if (!g2) {
        this._emit({ state: 'idle', error: 'Microphone permission denied.' });
        return;
      }
    }

    // If a jobId is provided, re-login with a job-scoped token so the TwiML
    // webhook can extract the jobId from the Twilio identity string and link
    // the call record automatically — no separate PATCH request needed.
    if (jobId) {
      const tokenRes = await apiService.getVoiceToken(jobId);
      await CapacitorTwilioVoice.login({ accessToken: tokenRes.token });
    }

    this._emit({
      state: 'connecting',
      toE164,
      callerIdE164,
      displayName,
      isMuted: false,
      audioRoute: 'earpiece',
      availableAudioDevices: [],
    });

    await CapacitorTwilioVoice.makeCall({
      to: toE164,
      callerId: callerIdE164,
      displayName,
    });
  }

  /**
   * Initiate a masked outbound call where the real destination is resolved server-side.
   *
   * The client sends an opaque reference ("contact:{contactId}:{primary|additional}") as
   * the `to` param. The TwiML webhook resolves it after verifying job + contact linkage.
   * No real E.164 destination is sent from the client; callerId is still client-supplied.
   *
   * @param jobId       Job this call is linked to (required — encodes masking context)
   * @param contactId   The contact's UUID
   * @param callerIdE164 The company's voice number to call from
   * @param which       Which phone to call: "primary" (default) or "additional"
   * @param displayName Human-readable label shown in the UI (e.g. "John ••• 1234")
   */
  async makeMaskedCall(
    jobId: string,
    contactId: string,
    callerIdE164: string,
    which: 'primary' | 'additional' = 'primary',
    displayName?: string,
  ): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      clientLogger.warn('[TwilioVoice] makeMaskedCall is only supported on native platforms.');
      return;
    }

    if (!this._initialized) {
      await this.init();
    }

    const { granted } = await CapacitorTwilioVoice.checkMicrophonePermission();
    if (!granted) {
      const { granted: g2 } = await CapacitorTwilioVoice.requestMicrophonePermission();
      if (!g2) {
        this._emit({ state: 'idle', error: 'Microphone permission denied.' });
        return;
      }
    }

    const opaqueRef = `contact:${contactId}:${which}`;

    try {
      // Re-login with a job-scoped token so the webhook can resolve the job context
      const tokenRes = await apiService.getVoiceToken(jobId);
      await CapacitorTwilioVoice.login({ accessToken: tokenRes.token });

      this._emit({
        state: 'connecting',
        // Use the opaque ref as the toE164 field so ActiveCallOverlay can distinguish masked calls
        toE164: opaqueRef,
        callerIdE164,
        displayName,
        isMuted: false,
        audioRoute: 'earpiece',
        availableAudioDevices: [],
      });

      await CapacitorTwilioVoice.makeCall({
        to: opaqueRef,
        callerId: callerIdE164,
        displayName,
      });
    } catch (err) {
      // Reset to idle so the UI is not stuck in a connecting state
      this._emit({ state: 'idle' });
      throw err;
    }
  }

  async endCall(callSid?: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    await CapacitorTwilioVoice.endCall({ callSid });
  }

  /** No-op: unclean-drop auto/one-tap redial is Telnyx-only (Twilio never emits `canRedial`). Present
   *  to satisfy the shared VoiceBackend surface. */
  async redial(): Promise<void> { /* Twilio softphone has no redial affordance */ }

  async muteCall(muted: boolean, callSid?: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    await CapacitorTwilioVoice.muteCall({ muted, callSid });
    this._emit({ ...this._currentState, isMuted: muted });
  }

  async getAudioDevices(): Promise<{ devices: AudioDevice[]; selected: AudioDeviceType }> {
    if (!Capacitor.isNativePlatform()) {
      return { devices: [{ type: 'earpiece', name: 'Phone' }], selected: 'earpiece' };
    }
    const result = await CapacitorTwilioVoice.getAudioDevices();
    return {
      devices: result.devices as AudioDevice[],
      selected: result.selected as AudioDeviceType,
    };
  }

  async selectAudioDevice(type: AudioDeviceType): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    await CapacitorTwilioVoice.selectAudioDevice({ type });
    await this._refreshAudioDevices();
  }

  async sendDigits(digits: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    await CapacitorTwilioVoice.sendDigits({ digits });
  }

  get currentState(): VoiceCallEvent {
    return { ...this._currentState };
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private _emit(event: VoiceCallEvent): void {
    this._currentState = event;
    this._listeners.forEach((l) => l({ ...event }));
  }

  private async _refreshAudioDevices(): Promise<void> {
    try {
      const { devices, selected } = await this.getAudioDevices();
      const previousDevices = this._currentState.availableAudioDevices ?? [];
      const effectiveDevices =
        devices.length > 0 ? devices : previousDevices;
      const effectiveRoute =
        devices.length > 0
          ? selected ?? devices[0]?.type ?? 'earpiece'
          : this._currentState.audioRoute ?? previousDevices[0]?.type ?? 'earpiece';

      this._emit({
        ...this._currentState,
        availableAudioDevices: effectiveDevices,
        audioRoute: effectiveRoute,
      });
    } catch (error) {
      clientLogger.warn('[TwilioVoice] Failed to refresh audio devices:', error);
    }
  }

  private async _fetchTokenAndLogin(jobId?: string): Promise<void> {
    const tokenRes = await apiService.getVoiceToken(jobId);
    await CapacitorTwilioVoice.login({ accessToken: tokenRes.token });

    // Schedule refresh at TTL − 60 s (min 30 s to avoid tight loops).
    // Refreshes always use the standard identity (no jobId) — this restores
    // the normal identity after a job-scoped call finishes.
    const refreshIn = Math.max((tokenRes.ttl - 60) * 1000, 30_000);
    this._scheduleRefresh(refreshIn);
  }

  private _scheduleRefresh(delayMs: number): void {
    this._clearRefreshTimer();
    this._refreshTimer = setTimeout(async () => {
      try {
        await this._fetchTokenAndLogin();
      } catch (err) {
        clientLogger.error('[TwilioVoice] Token refresh failed:', err);
      }
    }, delayMs);
  }

  private _clearRefreshTimer(): void {
    if (this._refreshTimer !== null) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  private async _registerListeners(): Promise<void> {
    // Outgoing call lifecycle
    await CapacitorTwilioVoice.addListener('outgoingCallInitiated', (data) => {
      this._emit({
        ...this._currentState,
        state: 'connecting',
        callSid: data.callSid,
      });
    });

    await CapacitorTwilioVoice.addListener('callRinging', (data) => {
      this._emit({ ...this._currentState, state: 'ringing', callSid: data.callSid });
    });

    await CapacitorTwilioVoice.addListener('callConnected', (data) => {
      // Emit connected immediately; audio devices arrive via audioDevicesChanged
      // once AudioSwitch finishes activating and BT SCO connects (~200-500 ms)
      this._emit({
        ...this._currentState,
        state: 'connected',
        callSid: data.callSid,
        isMuted: false,
      });

      void this._refreshAudioDevices();
    });

    await CapacitorTwilioVoice.addListener('audioDevicesChanged', (data) => {
      const devices = data.devices as AudioDevice[];
      const selected =
        (data.selected as AudioDeviceType | undefined) ??
        this._currentState.audioRoute ??
        devices[0]?.type ??
        'earpiece';

      if (this._currentState.state === 'connected') {
        const onEarpiece =
          !this._currentState.audioRoute || this._currentState.audioRoute === 'earpiece';
        const bluetoothAvailable = devices.some((d) => d.type === 'bluetooth');

        if (onEarpiece && bluetoothAvailable) {
          CapacitorTwilioVoice.selectAudioDevice({ type: 'bluetooth' }).catch((e) =>
            clientLogger.warn('[TwilioVoice] Failed to auto-select Bluetooth:', e),
          );
        }

        void this._refreshAudioDevices();
        return;
      }

      // Auto-select Bluetooth the first time it appears on a connected call
      // (only if still on default earpiece — respect any prior manual selection)
      this._emit({
        ...this._currentState,
        availableAudioDevices: devices,
        audioRoute: selected,
      });
    });

    await CapacitorTwilioVoice.addListener('callDisconnected', () => {
      this._emit({ state: 'ended' });
      // Auto-clear to idle after a brief pause so the UI can show "Call ended"
      setTimeout(() => this._emit({ state: 'idle' }), 2000);
    });

    await CapacitorTwilioVoice.addListener('outgoingCallFailed', (data) => {
      this._emit({
        state: 'ended',
        callSid: data.callSid,
        error: data.reason,
      });
      setTimeout(() => this._emit({ state: 'idle' }), 2000);
    });

    await CapacitorTwilioVoice.addListener('callReconnecting', (data) => {
      this._emit({ ...this._currentState, state: 'reconnecting', callSid: data.callSid, error: data.error });
    });

    await CapacitorTwilioVoice.addListener('callReconnected', (data) => {
      this._emit({ ...this._currentState, state: 'connected', callSid: data.callSid, error: undefined });
    });

    await CapacitorTwilioVoice.addListener('callQualityWarningsChanged', (data) => {
      this._emit({ ...this._currentState, qualityWarnings: data.currentWarnings as string[] });
    });
  }
}

export const twilioVoiceManager = TwilioVoiceManager.instance;
